import { CheckCircleIcon } from "@heroicons/react/24/outline";

import { Button } from "shared/ui/shadcn/button";
import { clsxMerge } from "shared/lib/helpers";
import { DayJs } from "shared/lib/helpers/date";

interface DetectedMeetingI {
  meeting_time: string;
  event_id?: string;
}

interface ConfirmDetectedStepPropsI {
  detectedMeeting: DetectedMeetingI;
  contactName?: string;
  accountName?: string;
  meetingTakerName: string;
  notes: string;
  notesError: boolean;
  isLoading: boolean;
  onNotesChange: (notes: string) => void;
  onConfirm: () => void;
}

export const ConfirmDetectedStep = ({
  detectedMeeting,
  contactName,
  accountName,
  meetingTakerName,
  notes,
  notesError,
  isLoading,
  onNotesChange,
  onConfirm,
}: ConfirmDetectedStepPropsI) => {
  const meetingDateTime = DayJs(detectedMeeting.meeting_time);

  return (
    <div>
      {/* Success message */}
      <div className="mb-6 rounded-lg border border-green-500 bg-green-50 p-3">
        <div className="flex items-center gap-2">
          <CheckCircleIcon className="h-5 w-5 text-green-600" />
          <p className="font-medium text-green-800">
            Meeting has been detected on the calendar!
          </p>
        </div>
      </div>

      {/* Meeting details */}
      <section className="mb-6 flex gap-8">
        <div className="flex flex-col gap-3">
          <div className="typography-body-4-medium">Lead name</div>
          <div className="typography-body-4-medium">Company name</div>
          <div className="typography-body-4-medium">Meeting with</div>
          <div className="typography-body-4-medium">Meeting time</div>
          <div className="typography-body-4-medium">Disposition</div>
        </div>

        <div className="flex flex-col gap-3 [&>div]:line-clamp-1 [&>div]:leading-[1.4]">
          <div
            title={contactName || "Prospect title not available"}
            className="text-base-content/60 typography-body-4"
          >
            {contactName || "N/A"}
          </div>

          <div
            title={accountName || "Account name not available"}
            className="text-base-content/60 typography-body-4"
          >
            {accountName || "N/A"}
          </div>

          <div
            title={meetingTakerName || "Meeting taker name not available"}
            className="text-base-content/60 typography-body-4"
          >
            {meetingTakerName}
          </div>

          <div
            title="Meeting date"
            className="text-base-content/60 typography-body-4"
          >
            {meetingDateTime.format("MMMM D, YYYY [at] h:mm A")}
          </div>

          <div className="text-base-content/60 typography-body-4">
            Meeting booked
          </div>
        </div>
      </section>

      {/* Notes */}
      <section className="mb-6">
        <label className="label">
          <span className="typography-body-4-medium">Notes</span>
        </label>

        <textarea
          name="notes"
          value={notes}
          onChange={(ev) => onNotesChange(ev.target.value)}
          rows={5}
          maxLength={2000}
          placeholder="Add notes about the meeting, lead qualification, or any other relevant information..."
          className={clsxMerge(
            "textarea w-full resize-none rounded-lg bg-black/5 p-4 typography-body-4 focus:outline-none",
            notesError && "textarea-bordered border-error-content"
          )}
        />

        {notesError && (
          <p className="mt-1 text-sm text-error-content">
            Please provide notes for the meeting
          </p>
        )}
      </section>

      {/* Submit button */}
      <section className="flex w-full justify-end">
        <Button
          isLoading={isLoading}
          onClick={onConfirm}
          disabled={isLoading}
          className="w-[140px]"
        >
          {isLoading ? "Finishing..." : "Book meeting"}
        </Button>
      </section>
    </div>
  );
};
